/* eslint-disable no-underscore-dangle */
import React, { useEffect, useState, useMemo } from 'react';
import {
  Modal, Button, Tab, Tabs, Spinner, Alert, Badge, Form,
} from 'react-bootstrap';
import Error from '../../../Components/Error';
import PricingService from '../../../Services/PricingService';
import OperatorVehicleBandsEditor from './OperatorVehicleBandsEditor';
import OperatorZoneOverridesTable from './OperatorZoneOverridesTable';
import OperatorRouteOverridesTable from './OperatorRouteOverridesTable';
import OperatorOnlyZonesEditor from './OperatorOnlyZonesEditor';
import OperatorOnlyRoutesEditor from './OperatorOnlyRoutesEditor';
import { emptyProfile, sanitizeProfileForApi } from './operatorPricingProfile';

function OperatorPricingProfileEditor({
  operator,
  show = false,
  handleClose,
}) {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('vehicles');
  const [vehicleCategories, setVehicleCategories] = useState([]);
  const [serviceAreas, setServiceAreas] = useState([]);
  const [predefinedRoutes, setPredefinedRoutes] = useState([]);

  const settlementCurrency = operator?.settlement_currency || 'EUR';

  // Load reference data + existing profile.
  useEffect(() => {
    if (!show || !operator?.Id) return undefined;
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const [vcRes, saRes, prRes] = await Promise.all([
          PricingService.fetchVehicleCategories(),
          PricingService.fetchServiceAreas(),
          PricingService.fetchPredefinedRoutes(),
        ]);
        if (cancelled) return;
        setVehicleCategories(vcRes?.data?.data || []);
        setServiceAreas(saRes?.data?.data || []);
        setPredefinedRoutes(prRes?.data?.data || []);

        try {
          const profileRes = await PricingService.fetchOperatorPricingProfile(operator.Id);
          if (cancelled) return;
          // Backend returns `{ profile: <ProfileObj> | null }`. The middleware
          // wraps that as `{ success, data: { profile: ... } }`, so the actual
          // payload sits two levels deep on the axios response.
          const loaded = profileRes?.data?.data?.profile || null;
          if (loaded && loaded.operator_id) {
            setProfile(loaded);
          } else {
            setProfile(emptyProfile(operator.Id, settlementCurrency));
          }
        } catch (err) {
          if (cancelled) return;
          if (err?.response?.status === 404) {
            setProfile(emptyProfile(operator.Id, settlementCurrency));
          } else {
            setError(err?.response?.data?.message || err.message || 'Failed to load profile');
            setProfile(emptyProfile(operator.Id, settlementCurrency));
          }
        }
      } catch (err) {
        if (cancelled) return;
        setError(err?.response?.data?.message || err.message || 'Failed to load reference data');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [show, operator?.Id, settlementCurrency]);

  const handleSave = async () => {
    if (!profile) return;
    if (profile.currency !== settlementCurrency) {
      setError(`Profile currency (${profile.currency}) must match operator settlement currency (${settlementCurrency}).`);
      return;
    }
    // Validate operator-only zones have a name and valid geo.
    const invalidZone = (profile.operator_only_zones || []).find((z) => !z.name?.trim());
    if (invalidZone) {
      setError('All operator-only zones must have a name.');
      return;
    }
    const invalidRoute = (profile.operator_only_routes || []).find((r) => !r.name?.trim());
    if (invalidRoute) {
      setError('All operator-only routes must have a name.');
      return;
    }
    // Validate override modes have at least one numeric field.
    const badZoneOverride = (profile.zone_overrides || []).find((zo) => {
      if (zo.mode !== 'override') return false;
      const hasMul = zo.price_multiplier !== undefined && zo.price_multiplier !== null;
      const hasFlat = zo.flat_rate !== undefined && zo.flat_rate !== '' && zo.flat_rate !== null;
      return !hasMul && !hasFlat;
    });
    if (badZoneOverride) {
      setError('Each zone override must specify a multiplier or flat rate.');
      return;
    }

    setSaving(true);
    setError('');
    try {
      const payload = sanitizeProfileForApi(profile);
      await PricingService.saveOperatorPricingProfile(payload);
      handleClose();
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!operator?.Id) return;
    // eslint-disable-next-line no-alert
    if (!window.confirm('Delete this operator\'s pricing profile? Quotes will fail until a new profile is created.')) {
      return;
    }
    setSaving(true);
    setError('');
    try {
      await PricingService.deleteOperatorPricingProfile(operator.Id);
      handleClose();
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Failed to delete profile');
    } finally {
      setSaving(false);
    }
  };

  const unconfiguredCounts = useMemo(() => {
    if (!profile) return { zones: 0, routes: 0 };
    const activeZones = serviceAreas.filter((sa) => sa.active === 1);
    const activeRoutes = predefinedRoutes.filter((r) => r.active === 1);
    const zoneIds = new Set((profile.zone_overrides || []).map((zo) => zo.service_area_id));
    const routeIds = new Set((profile.route_overrides || []).map((ro) => ro.predefined_route_id));
    return {
      zones: activeZones.filter((sa) => !zoneIds.has(sa.Id)).length,
      routes: activeRoutes.filter((r) => !routeIds.has(r.Id)).length,
    };
  }, [profile, serviceAreas, predefinedRoutes]);

  return (
    <Modal show={show} onHide={handleClose} size="xl">
      <Modal.Header closeButton>
        <Modal.Title>
          {`Pricing Profile — ${operator?.name || ''}`}
          <Badge bg="secondary" className="ms-2">{settlementCurrency}</Badge>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Error error={error} setError={setError} />

        {loading || !profile ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="text-muted mt-3">Loading profile…</p>
          </div>
        ) : (
          <>
            {(unconfiguredCounts.zones > 0 || unconfiguredCounts.routes > 0) && (
              <Alert variant="warning" className="py-2">
                <i className="fa fa-exclamation-triangle me-2" />
                {`${unconfiguredCounts.zones} zone(s) and ${unconfiguredCounts.routes} route(s) are unconfigured. Operator quotes will FAIL for trips that match those zones/routes until you mark them Opt out or Override.`}
              </Alert>
            )}

            <div className="border rounded p-3 mb-3 bg-light">
              <div className="fw-bold mb-2">Deadhead billing</div>
              <div className="text-muted small mb-2">
                Each empty-mile leg is billed independently. Default: both off
                — only passenger-carrying journey km are billed.
              </div>
              <Form.Check
                type="switch"
                id="bill-deadhead-to-pickup-switch"
                className="mb-1"
                label={(
                  <span>
                    <strong>Bill base → pickup</strong>
                    <span className="text-muted ms-2 small">
                      Empty miles from the operator&apos;s base to the pickup
                      point added to the band distance.
                    </span>
                  </span>
                )}
                checked={profile.bill_deadhead_to_pickup === true}
                onChange={(e) => setProfile((p) => ({
                  ...p,
                  bill_deadhead_to_pickup: e.target.checked,
                }))}
              />
              <Form.Check
                type="switch"
                id="bill-return-to-base-switch"
                label={(
                  <span>
                    <strong>Bill dropoff → base</strong>
                    <span className="text-muted ms-2 small">
                      Empty miles back to the operator&apos;s base after dropoff
                      added to the band distance.
                    </span>
                  </span>
                )}
                checked={profile.bill_return_to_base === true}
                onChange={(e) => setProfile((p) => ({
                  ...p,
                  bill_return_to_base: e.target.checked,
                }))}
              />
            </div>

            <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mb-3">
              <Tab eventKey="vehicles" title="Vehicle bands">
                <OperatorVehicleBandsEditor
                  vehicleBands={profile.vehicle_bands}
                  vehicleCategories={vehicleCategories}
                  currency={profile.currency}
                  onChange={(vehicleBands) => (
                    setProfile((p) => ({ ...p, vehicle_bands: vehicleBands }))
                  )}
                />
              </Tab>
              <Tab
                eventKey="zones"
                title={(
                  <>
                    Zone overrides
                    {unconfiguredCounts.zones > 0 && (
                      <Badge bg="warning" text="dark" className="ms-2">{unconfiguredCounts.zones}</Badge>
                    )}
                  </>
                )}
              >
                <OperatorZoneOverridesTable
                  serviceAreas={serviceAreas}
                  zoneOverrides={profile.zone_overrides}
                  currency={profile.currency}
                  onChange={(zoneOverrides) => (
                    setProfile((p) => ({ ...p, zone_overrides: zoneOverrides }))
                  )}
                />
              </Tab>
              <Tab
                eventKey="routes"
                title={(
                  <>
                    Route overrides
                    {unconfiguredCounts.routes > 0 && (
                      <Badge bg="warning" text="dark" className="ms-2">{unconfiguredCounts.routes}</Badge>
                    )}
                  </>
                )}
              >
                <OperatorRouteOverridesTable
                  predefinedRoutes={predefinedRoutes}
                  routeOverrides={profile.route_overrides}
                  vehicleCategories={vehicleCategories}
                  currency={profile.currency}
                  onChange={(routeOverrides) => (
                    setProfile((p) => ({ ...p, route_overrides: routeOverrides }))
                  )}
                />
              </Tab>
              <Tab eventKey="own-zones" title="Operator-only zones">
                <OperatorOnlyZonesEditor
                  zones={profile.operator_only_zones}
                  onChange={(zones) => setProfile((p) => ({ ...p, operator_only_zones: zones }))}
                />
              </Tab>
              <Tab eventKey="own-routes" title="Operator-only routes">
                <OperatorOnlyRoutesEditor
                  routes={profile.operator_only_routes}
                  vehicleCategories={vehicleCategories}
                  onChange={(routes) => setProfile((p) => ({ ...p, operator_only_routes: routes }))}
                />
              </Tab>
            </Tabs>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-danger" onClick={handleDelete} disabled={saving || loading}>
          <i className="fa fa-trash me-1" />
          Delete profile
        </Button>
        <Button variant="secondary" onClick={handleClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={saving || loading}>
          {saving ? (
            <>
              <Spinner size="sm" className="me-2" />
              Saving…
            </>
          ) : (
            'Save profile'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default OperatorPricingProfileEditor;
